"use client"

import { createContext, useContext, useState, useEffect } from "react"
import { message } from "antd"
import { apiRequest } from '../utils/apiRequest'
import { useAuth } from "./AuthContext"

const WalletContext = createContext(undefined)

export const WalletProvider = ({ children }) => {
  const [balance, setBalance] = useState(0)
  const [totalOrders, setTotalOrders] = useState(0)
  const [recentOrders, setRecentOrders] = useState([])
  const [totalSpent, setTotalSpent] = useState(0)
  const [currencies, setCurrencies] = useState([])
  const [loading, setLoading] = useState(false)
  const [peachDepositInProgress, setPeachDepositInProgress] = useState(null) // Track Peach deposits
  const { user, isAuthenticated } = useAuth()

  useEffect(() => {
      getWallet()
  }, [user])

  // POLLING FOR PEACH DEPOSIT COMPLETION
  useEffect(() => {
    if (!peachDepositInProgress) return;

    const checkPeachDepositCompletion = async () => {
      try {
        // Get current wallet balance
        const response = await apiRequest({
          method: "GET",
          url: "/wallet",
        });

        if (response.status === 200) {
          const newBalance = parseFloat(response.data.balance);

          // Check if balance increased (indicating successful deposit)
          if (newBalance > peachDepositInProgress.originalBalance) {
            console.log('Peach deposit completed! Balance updated from', peachDepositInProgress.originalBalance, 'to', newBalance);

            // Update local state
            setBalance(newBalance);
            setRecentOrders(response.data.recentOrders);
            setTotalOrders(parseFloat(response.data.total_orders));
            setTotalSpent(response.data.total_spent);
            setCurrencies(response.data.currencies);

            // Close modal and reset form
            if (peachDepositInProgress.callbacks) {
              peachDepositInProgress.callbacks.onModalClose?.();
              peachDepositInProgress.callbacks.onFormReset?.();
            }

            // Show success notification
            message.success('Deposit successful! Wallet balance updated.');

            // Clear the deposit tracking
            setPeachDepositInProgress(null);
          }
        }
      } catch (error) {
        console.error('Error checking Peach deposit status:', error);
      }
    };

    // Check every 3 seconds for deposit completion
    const interval = setInterval(checkPeachDepositCompletion, 3000);

    // Cleanup interval
    return () => clearInterval(interval);
  }, [peachDepositInProgress]);

  const getWallet = async () => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "GET",
        url: "/wallet",
      })

      if (response.status === 200) {
        setBalance(parseFloat(response.data.balance))
        setRecentOrders(response.data.recentOrders)
        setTotalOrders(parseFloat(response.data.total_orders))
        setTotalSpent(response.data.total_spent)
        setCurrencies(response.data.currencies)
      }
    } catch (error) {
      console.error("Failed to fetch wallet:", error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Convert a selling price back to the amount the recipient receives.
   *
   * Markup now cascades (product > category > provider > currency), so the
   * currency row alone is not enough to invert the price. Callers that know the
   * item pass the server-resolved `markupPercent` (products/categories expose it
   * on the product payload); the currency-level value is only a fallback for
   * callers that don't.
   */
  const rate = (price, currency, markupPercent = null) => {
    if(!currencies || currencies.length < 1){
      getWallet()
    }

    if(currencies.length < 1) return
    const currencyData = currencies.find(c => c.code?.toUpperCase() === currency?.toUpperCase());
    if(!currencyData) return
    const { SYS_EXC_RATE, markup_percent } = currencyData;
    const effectiveMarkup = markupPercent ?? markup_percent ?? 0;
    const markupMultiplier = 1 + (effectiveMarkup / 100);
    const divisor = SYS_EXC_RATE * markupMultiplier;
    if(!divisor) return
    return (price / divisor);
  }

  const topup = async (amount, paymentMethod, options = null) => {
    try {
      if (amount <= 0) return { success: false, error: "Amount must be positive" }

      setLoading(true)

      // Prepare request data
      const requestData = { amount, payment_method: paymentMethod }

      // Add voucher data for OTT and 1Voucher
      if ((paymentMethod === "OTT Voucher" || paymentMethod === "1Voucher") && options && options.voucherPin && options.mobile) {
        requestData.voucherPin = options.voucherPin
        requestData.mobile = options.mobile
      }

      const response = await apiRequest({
        method: "POST",
        url: "/wallet/topup",
        data: requestData,
        endpointType: 'main'
      })

      // For OTT and 1Voucher, return success directly (no redirect needed)
      if (paymentMethod === "OTT Voucher" || paymentMethod === "1Voucher") {
        if (response.status === 200 && response.data.status === 'complete') {
          await getWallet() // Refresh wallet balance
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.data?.message || "Voucher redemption failed" }
        }
      }

      // For other payment methods, handle redirects
      if (response.status === 200 && response.data.checkout_url) {
        if (paymentMethod === 'Peach' || paymentMethod === 'peach_payment_links') {
          // Peach: Open in new tab and show info message
          console.log(`Opening Peach deposit URL in new tab:`, response.data.checkout_url);
          message.info('Complete your deposit in the new tab. Your wallet will be updated automatically.', 5);

          // Track Peach deposit for automatic completion detection
          setPeachDepositInProgress({
            originalBalance: balance,
            startTime: Date.now(),
            amount: amount,
            callbacks: options // Store modal control callbacks
          });

          window.open(response.data.checkout_url, '_blank');
        } else {
          // Other payment methods: Redirect in current tab
          console.log(`Redirecting to ${paymentMethod} deposit URL:`, response.data.checkout_url);
          window.location.href = response.data.checkout_url;
        }
        return { success: true }
      } else {
        console.error("Topup response:", response);
        return { success: false, error: response.data?.message || "Failed to initiate payment" }
      }
    } catch (error) {
      console.error("Topup failed:", error)
      return {
        success: false,
        error: error.response?.data?.message || "Topup failed"
      }
    } finally {
      setLoading(false)
    }
  }

  const transfer = async (recipientId, amount, description) => {
    try {
      if (amount <= 0) return { success: false, error: "Amount must be positive" }

      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: "/api/wallet/transfer",
        data: { recipientId, amount, description }
      })

      if (response.status === 200) {
        await getWallet() // Refresh wallet data
        return { success: true }
      }
    } catch (error) {
      console.error("Transfer failed:", error)
      return { 
        success: false, 
        error: error.response?.data?.message || "Transfer failed" 
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <WalletContext.Provider
      value={{
        balance,
        recentOrders,
        totalOrders,
        totalSpent,
        loading,
        getWallet,
        topup,
        transfer,
        rate
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export const useWallet = () => {
  const context = useContext(WalletContext)
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider")
  }
  return context
}
